import {getStabilityRuleFormDefaultValues} from '@/modules/drug-catalog/stability-rules/functions'
import {StabilityRuleFormSchema} from '@/modules/drug-catalog/stability-rules/schemas'
import {TradeNameFormSchema} from '@/modules/products/trade-names/schemas'
import type {StabilityStage} from '@cypro/engine/database/schema'
import {PatientTherapySequenceMedicine, PrescribedName, TradeName} from '@cypro/engine/schema'
import {getMissingRequiredStabilityStages} from '@cypro/engine/utils'
import {SelectItem} from '@cypro/ui/components/form/select'

export function getTradeNameFormDefaultValues(tradeName: TradeName | undefined): TradeNameFormSchema {
  const stabilityRules: StabilityRuleFormSchema[] = []
  if (tradeName?.stabilityRules) {
    for (const item of tradeName.stabilityRules) {
      if (item.stabilityRule) stabilityRules.push(getStabilityRuleFormDefaultValues(item.stabilityRule))
    }
  }

  const solvents = tradeName?.reconstitutionSolvents?.map(item => ({
    id: item.solutionDrugId,
    solventVolume: item.solventVolume || 0,
  }))

  return {
    additionalApprovalRequired: Boolean(tradeName?.additionalApprovalRequired),
    containsAlcohol: Boolean(tradeName?.containsAlcohol),
    atcCode: tradeName?.atcCode || '',
    containerContents: tradeName?.containerContents || undefined,
    containerMaterialId: tradeName?.containerMaterialId || undefined,
    containerTypeId: tradeName?.containerTypeId || undefined,
    prescribedName: tradeName?.prescribedNameId || '',
    drugCatalogItemId: tradeName?.medicationProfile?.drugCatalogItemId || '',
    isDrySubstance: Boolean(tradeName?.medicationProfile?.isDrySubstance),
    isTrial: Boolean(tradeName?.isTrial),
    medicationProfileId: tradeName?.medicationProfileId || '',
    name: tradeName?.name || '',
    strengthUnitId: tradeName?.strengthUnitId || '',
    strengthValue: tradeName?.strengthValue || 0,
    // @ts-expect-error
    type: tradeName?.medicationProfile?.drugCatalogItem?.catalogType || undefined,
    unitsPerPackage: tradeName?.unitsPerPackage || 1,
    minimumStock: tradeName?.minimumStock || 0,
    additionalsUsed: Boolean(tradeName?.additionalsUsed),
    additionalsDetails: tradeName?.additionalsDetails || '',
    additionalsWeight: tradeName?.additionalsWeight || 0,
    barcodeGtin: tradeName?.barcodeGtin || '',
    density: tradeName?.density || 0,
    description: tradeName?.description || '',
    productionNotes:
      tradeName?.productionNotes ||
      tradeName?.medicationProfile?.drugCatalogItem?.activeSubstanceDetails?.productionNotes ||
      '',
    externalInformationLink: tradeName?.externalInformationLink || '',
    labelName: tradeName?.labelName || '',
    manufacturerId: tradeName?.manufacturerId || undefined,
    volumeValue: tradeName?.volumeValue || 0,
    overfilledBy: tradeName?.overfilledBy || 0,
    actualVolume: tradeName?.actualVolume || 0,
    maxVolumeCapacity: tradeName?.maxVolumeCapacity || 0,
    concentrationValue: tradeName?.concentrationValue || 0,
    concentrationUnitId: tradeName?.concentrationUnitId || undefined,
    reconstitutionRequired: Boolean(
      tradeName?.reconstitutionRequired || tradeName?.medicationProfile?.reconstitutionRequired
    ),
    reconstitutedSolutionConcentration: tradeName?.reconstitutedSolutionConcentration || 0,
    reconstitutedConcentrationUnitId: tradeName?.reconstitutedConcentrationUnitId || '',
    reconstitutedSolutionVolumeExpansion: tradeName?.reconstitutedSolutionVolumeExpansion || 0,
    reconstitutionSolvents: solvents?.length ? solvents : [{id: '', solventVolume: 0}],
    consumables:
      tradeName?.consumables?.map(item => {
        return {id: item.consumableId, units: item.units ?? 0}
      }) || [],
    stabilityRules,
    nationalId: tradeName?.nationalId || '',
    minimumSaveableVolume: tradeName?.minimumSaveableVolume ?? undefined,
  }
}

export function mapTradeNamesToSelectItems(tradeNames: TradeName[]): SelectItem[] {
  return tradeNames.map(t => ({value: t.id, label: t.name}))
}

export function mapPrescribedNamesToSelectItems(prescribedNames: PrescribedName[]): SelectItem[] {
  return prescribedNames.map(p => ({value: p.id, label: p.name}))
}

/**
 * An aut-idem prescription is dispensed exactly as the doctor's product; with no
 * product named by the doctor yet there is nothing to be locked to, so the
 * pharmacist can still pick one.
 */
export function isPrescribedTradeNameLocked(medicine: PatientTherapySequenceMedicine | undefined | null) {
  return Boolean(
    medicine && Boolean(medicine.autIdem) && Boolean(medicine.prescribedName?.name || medicine.prescribedNameId)
  )
}

export function mapPrescribedNamesToSelectItemsWithCount(prescribedNames: PrescribedName[]) {
  return prescribedNames.map(p => {
    const count =
      p?.tradeNames?.reduce(
        (tradeNameAcc, tn) =>
          tradeNameAcc +
          (tn?.batches ? tn?.batches?.reduce((batchAcc, batch) => batchAcc + (batch?.quantity || 0), 0) : 0),
        0
      ) || 0
    return {value: p.id, label: p.name, count}
  })
}

/**
 * Returns the total amount of active drug (mg) in a single vial of the given trade name.
 * For both solids and liquids, this is `strengthValue` (the labeled total mg).
 */
export function getVialTotalDrug(tradeName: TradeName | undefined): number {
  if (!tradeName?.strengthValue) return 0
  return tradeName.strengthValue
}

export function getVialTotalCarrier(tradeName: TradeName | undefined): number {
  if (!tradeName?.volumeValue) return 0
  return tradeName.volumeValue
}

/**
 * Returns the concentration value the compounding engine expects.
 * For solids: returns the total drug per vial (mg).
 * For liquids: returns mg/mL, calculated as `strengthValue / volumeValue`.
 */
export function getTradeNameConcentration(tradeName: TradeName | undefined): number {
  if (!tradeName?.strengthValue) return 0
  const isSolid = Boolean(tradeName.medicationProfile?.isDrySubstance)
  if (isSolid) return tradeName.strengthValue
  if (tradeName.volumeValue) return tradeName.strengthValue / tradeName.volumeValue
  return 0
}

/** Open and Prepared rules are mandatory; a dry substance is only opened once reconstituted. */
export const REQUIRED_STABILITY_STAGES = (isDrySubstance: boolean): readonly StabilityStage[] => [
  isDrySubstance ? 'AFTER_RECONSTITUTION' : 'AFTER_OPENING',
  'AFTER_PREPARATION',
]

type StabilityStageLike = {stabilityStage?: string | string[] | null}

function collectStabilityStages(rules: readonly StabilityStageLike[]): string[] {
  const stages: string[] = []
  for (const rule of rules) {
    const value = rule.stabilityStage
    for (const stage of Array.isArray(value) ? value : value ? [value] : []) stages.push(stage)
  }
  return stages
}

/** Open and Prepared rules must be assigned to an active-substance trade name itself (CY-412). */
export function getMissingStabilityStages(input: {
  type: string | undefined
  isDrySubstance: boolean
  ownRules: readonly StabilityStageLike[]
}): string[] {
  return getMissingRequiredStabilityStages({
    type: input.type,
    isDrySubstance: input.isDrySubstance,
    ownStages: collectStabilityStages(input.ownRules),
  })
}
